// Directly copied from the mobile repo
// mobile repocommit: aaf81442825d53639430623a62bbaf7b19ed1411

import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';

class CurrentDateService {
  currentDate: DateTime;
  currentDateStr: string;
  _listeners: {
    [key: string]: (currentDate: DateTime) => void,
  };
  _counter = 0;

  constructor() {
    this.currentDate = DateTime.now().startOf('day');
    this.currentDateStr = this.currentDate.toISODate()!;
    this._listeners = {};
    setInterval(() => {
      const today = DateTime.now().startOf('day');
      if (today != this.currentDate) {
        this.currentDate = today;
        this.currentDateStr = today.toISODate();
        Object.values(this._listeners).forEach(listener => listener(today));
      }
    }, 60 * 1000);
  }
}

export const currentDateService = new CurrentDateService();

/**
 * Returns the current date as a `DateTime` object, updating every minute.
 */
export const useCurrentDate = (): DateTime => {
  // Note: this function was showing up as hot in the profiler, so use a more
  // performant method of updating the state.
  const [currentDate, setCurrentDate] = useState(currentDateService.currentDate);
  useEffect(() => {
    const id = ++currentDateService._counter;
    currentDateService._listeners[id] = setCurrentDate;
    return () => {
      delete currentDateService._listeners[id];
    };
  }, []);
  return currentDate;
};

let _today: {
  date: string,
  expiresEpoch: number,
} | null = null;

let _nowApprox: {
  date: DateTime,
  expiresEpoch: number,
} | null = null;

/**
 * Returns the current date in ISO format.
 *
 * Note: this function is used because `DateTime` operations can be a little bit
 * slow, and doing thigns this way speeds them up.
 *
 * Note: this function is cached so that it can be called multiple times
 * in a row without recalculating the date.
 */
export const fastGetToday = () => {
  if (!_today || _today.expiresEpoch < Date.now()) {
    const now = DateTime.now();
    _today = {
      date: now.toISODate(),
      expiresEpoch: now.endOf('day').plus({ minutes: 1 }).toMillis(),
    };
  }
  return _today.date;
};

/**
 * Returns an approximation of the current time as a DateTime object.
 *
 * Note: This function caches the DateTime object for one minute to improve performance
 * when called multiple times in a row. The returned time will be
 * accurate to within one minute of the actual time.
 */
export const fastGetNowApprox = () => {
  if (!_nowApprox || _nowApprox.expiresEpoch < Date.now()) {
    const now = DateTime.now();
    _nowApprox = {
      date: now,
      expiresEpoch: now.plus({ minutes: 1 }).toMillis(),
    };
  }
  return _nowApprox.date;
};

/**
 * Returns true if the timestamp is today, false otherwise.
 */
export const timestampIsToday = (timestamp: string | undefined) => {
  if (!timestamp) {
    return false;
  }
  return timestamp.startsWith(fastGetToday());
};

/**
 * Returns the start and end of the day for a given date.
 *
 * Note: this function is faster than using `DateTime.startOf('day')` and
 * `DateTime.endOf('day')` because it avoids the need to create a new `DateTime`
 * object.
 */
export function fasterStartEndOfDay(date: DateTime): [Date, Date] {
  if (!date.isValid) {
    throw new Error('fasterStartEndOfDay: date is not a valid DateTime');
  }
  const res = date.toJSDate();
  return [
    new Date(res.setHours(0, 0, 0, 0)),
    new Date(res.setHours(23, 59, 59, 999)),
  ];
}

/**
 * Returns the milliseconds since epoch for a given date string.
 *
 * Note: this function is faster than using `DateTime.toMillis()` because it
 * avoids the need to create a new `DateTime` object.
 */
export function fasterDateTimeStringToMillis(date: string): number {
  return +new Date(date);
}
